import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from '../ui/Button';
import { WeightUnitToggle } from './WeightUnitToggle';
import { updateDeliveryItemLogistics } from '../../features/deliveryItems/deliveryItemsThunks';
import {
  convertWeightInput,
  gramsToUnit,
  unitToGrams,
  useWeightUnit,
  weightFieldProps,
} from '../../features/deliveryItems/weightUnit';

const DIMENSIONS = [
  { name: 'height_cm', label: 'Alto (cm)' },
  { name: 'width_cm', label: 'Ancho (cm)' },
  { name: 'length_cm', label: 'Largo (cm)' },
];

const toNumber = (value) => (value === '' || value === null ? null : parseInt(value, 10));

/**
 * The weight, the dimensions and the contents of one bulto.
 *
 * One form for the two places that edit a bulto: the "Variables logísticas"
 * modal of the order detail and the bultos modal of the deliveries listing.
 * The rules live here once: the per-product counters are capped by what is
 * still unassigned (the same unit cannot travel in two bultos, so the ceiling
 * of each field is the ordered quantity minus whatever the other bultos
 * already claim), and an over-allocation cannot be saved.
 *
 * The weight is typed in kilos by default, or in grams on request (the check
 * next to the measurements). That is display only: the backend keeps and
 * receives `weight_grams`, and this form always converts before sending.
 *
 * @param {object} props
 * @param {object} props.item - The bulto being edited.
 * @param {Array<object>} props.items - Every bulto of the order, for the ceilings.
 * @param {Array<object>} props.orderProducts - The order's `products`.
 * @param {number|string|undefined} props.deliveryId - For the delivery's total weight.
 * @param {() => void} props.onSaved
 * @param {() => void} props.onCancel
 * @param {(message: string) => void} props.onError
 * @param {(busy: boolean) => void} [props.onBusyChange] - Tells the dialog
 *   around the form when a save is on its way, so it does not let itself be
 *   closed in the middle of it.
 * @param {'kg'|'g'} [props.unit] - The weight unit, when the dialog around
 *   the form owns the choice; otherwise the form keeps its own.
 * @param {(unit: 'kg'|'g') => void} [props.onUnitChange]
 * @param {string} [props.cancelLabel='Cancelar']
 */
export const BultoLogisticsForm = ({
  item,
  items,
  orderProducts = [],
  deliveryId,
  onSaved,
  onCancel,
  onError,
  onBusyChange,
  unit: unitProp,
  onUnitChange,
  cancelLabel = 'Cancelar',
}) => {
  const dispatch = useDispatch();
  const [ownUnit, setOwnUnit] = useWeightUnit();
  const unit = unitProp ?? ownUnit;
  const setUnit = onUnitChange ?? setOwnUnit;

  const [weight, setWeight] = useState('');
  const [form, setForm] = useState({});
  const [quantities, setQuantities] = useState({});
  const [saving, setSavingState] = useState(false);
  const setSaving = (value) => {
    setSavingState(value);
    onBusyChange?.(value);
  };

  // Seeded in the unit in force at that moment...
  const unitInField = useRef(unit);
  useEffect(() => {
    if (!item) return;
    unitInField.current = unit;
    const seeded = gramsToUnit(item.weight_grams, unit);
    setWeight(seeded === null ? '' : String(seeded));
    setForm({
      height_cm: item.height_cm || '',
      width_cm: item.width_cm || '',
      length_cm: item.length_cm || '',
    });
    setQuantities(
      (item.item_products || []).reduce((acc, assignment) => {
        acc[assignment.order_product] = String(assignment.prepared_quantity);
        return acc;
      }, {})
    );
    // The unit is handled by the effect below: re-seeding on its change would
    // throw away what was typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  // ...and re-typed, not reset, when the unit changes under it.
  useEffect(() => {
    if (unitInField.current === unit) return;
    setWeight((current) => convertWeightInput(current, unitInField.current, unit));
    unitInField.current = unit;
  }, [unit]);

  if (!item) return null;

  /** Units of `product` not already claimed by the other bultos. */
  const availableFor = (product) => {
    const claimedElsewhere = (items || [])
      .filter((other) => other.id !== item.id)
      .reduce((sum, other) => {
        const assignment = (other.item_products || []).find(
          (entry) => entry.order_product === product.id
        );
        return sum + (assignment?.prepared_quantity || 0);
      }, 0);
    return product.quantity - claimedElsewhere;
  };

  /**
   * Products whose quantity in this bulto exceeds what is left of them. It
   * can be there from the start: adding a bulto copies the contents of the
   * previous one, which double-books whatever it was carrying.
   */
  const overAllocated = orderProducts.filter(
    (product) => parseInt(quantities[product.id] || '0', 10) > availableFor(product)
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      await dispatch(
        updateDeliveryItemLogistics({
          itemId: item.id,
          deliveryId,
          values: {
            // Grams, whatever the field shows: the TMS integrations read grams.
            weight_grams: unitToGrams(weight, unit),
            height_cm: toNumber(form.height_cm),
            width_cm: toNumber(form.width_cm),
            length_cm: toNumber(form.length_cm),
            products: orderProducts.map((product) => ({
              order_product_id: product.id,
              prepared_quantity: parseInt(quantities[product.id] || '0', 10),
            })),
          },
        })
      ).unwrap();
      onSaved();
    } catch (error) {
      onError(error);
    } finally {
      setSaving(false);
    }
  };

  const weightField = weightFieldProps(unit);

  return (
    <div data-testid="bulto-logistics-form">
      <div className="flex flex-col gap-6 max-h-[60vh] overflow-y-auto pr-1">
        {orderProducts.length > 0 && (
          <div>
            <span className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-2">
              Productos del bulto
            </span>
            <div className="border border-border rounded-xl overflow-hidden divide-y divide-border">
              {orderProducts.map((product) => {
                const available = availableFor(product);
                const current = quantities[product.id] ?? '';
                const isOver = parseInt(current || '0', 10) > available;
                const name = product.product?.name || 'Producto';

                return (
                  <div
                    key={product.id}
                    className={`flex items-center gap-3 px-4 py-2.5 ${isOver ? 'bg-red-50 dark:bg-red-900/20' : ''}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-ninja-dark dark:text-white truncate">{name}</p>
                      <p className="text-xs text-text-muted">
                        {available} disp. / {product.quantity} total
                      </p>
                    </div>
                    <input
                      type="number"
                      min="0"
                      max={available}
                      placeholder="0"
                      aria-label={`Cantidad de ${name} en este bulto`}
                      value={current}
                      onChange={(e) =>
                        setQuantities((prev) => ({ ...prev, [product.id]: e.target.value }))
                      }
                      className={`w-20 text-center bg-transparent border rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-ninja-green transition-colors ${isOver ? 'border-danger' : 'border-border'
                        }`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between gap-3 mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-text-muted">
              Medidas y peso
            </span>
            <WeightUnitToggle unit={unit} onChange={setUnit} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1" htmlFor="bulto-weight">
                {weightField.label}
              </label>
              <input
                id="bulto-weight"
                type="number"
                inputMode="decimal"
                min={weightField.min}
                step={weightField.step}
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                className="w-full bg-transparent border border-border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-ninja-green transition-colors"
              />
            </div>
            {DIMENSIONS.map((field) => (
              <div key={field.name}>
                <label className="block text-xs font-medium text-text-secondary mb-1" htmlFor={`bulto-${field.name}`}>
                  {field.label}
                </label>
                <input
                  id={`bulto-${field.name}`}
                  type="number"
                  min="1"
                  value={form[field.name] ?? ''}
                  onChange={(e) => setForm((prev) => ({ ...prev, [field.name]: e.target.value }))}
                  className="w-full bg-transparent border border-border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-ninja-green transition-colors"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {overAllocated.length > 0 && (
        <p className="mt-4 text-sm text-danger">
          {overAllocated.map((product) => `«${product.product?.name || 'Producto'}»`).join(', ')}
          {overAllocated.length === 1 ? ' excede' : ' exceden'} lo disponible: ya
          {overAllocated.length === 1 ? ' está asignado' : ' están asignados'} en otro bulto. Baja la
          cantidad para poder guardar.
        </p>
      )}

      <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-border">
        <Button variant="subtle" pill onClick={onCancel} disabled={saving}>
          {cancelLabel}
        </Button>
        <Button
          variant="primary"
          pill
          onClick={handleSave}
          loading={saving}
          loadingLabel="Guardando..."
          disabled={overAllocated.length > 0}
        >
          Guardar
        </Button>
      </div>
    </div>
  );
};
